import type { EChartsOption } from 'echarts'

/**
 * Theme ECharts coerenti con il design system AWP.
 * Registrati sul global `echarts` (vedi echarts-setup) come 'awp-rtu' (chiaro)
 * e 'awp-rtu-dark' (scuro). Il tema scuro è il remap dei soli neutri previsto
 * dalla "Bridge at Night Rule": le tinte di stato restano identiche.
 */
export const RTU_ECHARTS_THEME_NAME = 'awp-rtu'
export const RTU_ECHARTS_THEME_DARK_NAME = 'awp-rtu-dark'

/**
 * Font dei grafici ECharts. Il tema viene costruito a module-load: risolvendo
 * `--font-app` (interruttore globale font in src/styles.css) i canvas seguono
 * l'attivazione/disattivazione di Quicksand senza modifiche qui.
 * Fallback: stack di sistema se il token non è disponibile.
 */
const FALLBACK_FONT_FAMILY = '-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'

function resolveAppFontFamily (): string {
  if (typeof document === 'undefined') {
    return FALLBACK_FONT_FAMILY
  }
  return getComputedStyle(document.documentElement).getPropertyValue('--font-app').trim() || FALLBACK_FONT_FAMILY
}

/** true se l'utente/OS chiede meno movimento (ECharts non lo rispetta da solo). */
export function prefersReducedMotion (): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/**
 * Default di animazione per i grafici ECharts: ingresso breve e "morphing" sui
 * refresh (update), così i valori si spostano invece di lampeggiare.
 * Con prefers-reduced-motion l'animazione è spenta del tutto.
 *
 * Nota: valutato al caricamento del tema (un cambio di impostazione OS richiede
 * un reload). Cucire qui i default comuni evita animazioni diverse per grafico.
 */
export function chartMotion (overrides: Partial<EChartsOption> = {}): Partial<EChartsOption> {
  if (prefersReducedMotion()) {
    return { animation: false }
  }
  return {
    animation: true,
    animationDuration: 600,
    animationEasing: 'cubicOut',
    animationDurationUpdate: 320,
    animationEasingUpdate: 'cubicOut',
    ...overrides,
  }
}

const AXIS_LABEL_LIGHT = '#6b7280'
const AXIS_LINE_LIGHT = '#e5e7eb'
const SPLIT_LINE_LIGHT = '#f1f5f9'
const TEXT_LIGHT = '#334155'

const AXIS_LABEL_DARK = '#94a3b8'
const AXIS_LINE_DARK = '#334155'
const SPLIT_LINE_DARK = '#334155'
const TEXT_DARK = '#e2e8f0'

interface AxisPalette {
  axisLabel: string
  axisLine: string
  splitLine: string
  text: string
  /** Traccia vuota di gauge/pie che non usa una tinta di stato. */
  track: string
  /** Bordo dei settori (separatore): superficie della card. */
  panelBorder: string
}

const LIGHT_PALETTE: AxisPalette = {
  axisLabel: AXIS_LABEL_LIGHT,
  axisLine: AXIS_LINE_LIGHT,
  splitLine: SPLIT_LINE_LIGHT,
  text: TEXT_LIGHT,
  track: '#e5e7eb',
  panelBorder: '#ffffff'
}

const DARK_PALETTE: AxisPalette = {
  axisLabel: AXIS_LABEL_DARK,
  axisLine: AXIS_LINE_DARK,
  splitLine: SPLIT_LINE_DARK,
  text: TEXT_DARK,
  track: '#334155',
  panelBorder: '#1e293b'
}

function buildTheme (p: AxisPalette) {
  return {
    color: ['#19549f', '#2b897a', '#D97706', '#DC2626', '#64748B', '#0ea5e9', '#8b5cf6'],
    backgroundColor: 'transparent',
    // Animazioni comuni a tutti i grafici con questo tema (enter + update).
    ...chartMotion(),
    textStyle: {
      fontFamily: resolveAppFontFamily(),
      color: p.text
    },
    title: {
      textStyle: { color: p.text, fontWeight: 700, fontSize: 13 },
      subtextStyle: { color: p.axisLabel }
    },
    legend: {
      textStyle: { color: p.axisLabel, fontSize: 11 },
      icon: 'circle'
    },
    tooltip: {
      backgroundColor: 'rgba(30, 41, 59, 0.96)',
      borderWidth: 0,
      padding: [8, 10],
      textStyle: { color: '#f8fafc', fontSize: 12 },
      axisPointer: {
        lineStyle: { color: '#94a3b8' },
        crossStyle: { color: '#94a3b8' },
        shadowStyle: { color: 'rgba(148,163,184,0.12)' }
      }
    },
    grid: {
      borderColor: p.axisLine
    },
    categoryAxis: {
      axisLine: { lineStyle: { color: p.axisLine } },
      axisTick: { show: false },
      axisLabel: { color: p.axisLabel, fontSize: 11 },
      splitLine: { show: false, lineStyle: { color: p.splitLine } }
    },
    valueAxis: {
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: p.axisLabel, fontSize: 11 },
      splitLine: { show: true, lineStyle: { color: p.splitLine } }
    },
    timeAxis: {
      axisLine: { lineStyle: { color: p.axisLine } },
      axisTick: { show: false },
      axisLabel: { color: p.axisLabel, fontSize: 11 },
      splitLine: { show: true, lineStyle: { color: p.splitLine } }
    },
    line: {
      smooth: true,
      symbol: 'circle',
      symbolSize: 6,
      lineStyle: { width: 2 }
    },
    bar: {
      itemStyle: { borderRadius: [3, 3, 0, 0] },
      // Cascata d'ingresso: le barre compaiono in sequenza (solo all'enter, non sull'update)
      animationDelay: (idx: number) => Math.min(idx * 30, 300)
    },
    pie: {
      itemStyle: { borderColor: p.panelBorder, borderWidth: 2 },
      animationDelay: (idx: number) => Math.min(idx * 40, 320)
    },
    gauge: {
      axisLine: { lineStyle: { color: [[1, p.track]] } }
    },
    visualMap: {
      textStyle: { color: p.axisLabel }
    }
  }
}

export const RTU_ECHARTS_THEME = buildTheme(LIGHT_PALETTE)
export const RTU_ECHARTS_THEME_DARK = buildTheme(DARK_PALETTE)

/**
 * Colori inline usati dagli option-builder che non passano dal tema (mappa,
 * sunburst, gauge, grafo diagnostica). Centralizzati qui per non lasciare
 * hex "solo-chiari" sparsi nelle utility dei grafici.
 */
export function rtuChartTokens (dark: boolean) {
  const p = dark ? DARK_PALETTE : LIGHT_PALETTE
  return {
    /** Traccia vuota di gauge (SLA / indice criticità). */
    gaugeTrack: p.track,
    /** Bordo dei settori sunburst. */
    panelBorder: p.panelBorder,
    /** Geo della mappa Italia. */
    mapArea: dark ? '#1e293b' : '#eef2f7',
    mapBorder: dark ? '#334155' : '#cbd5e1',
    mapAreaEmphasis: dark ? '#334155' : '#e2e8f0',
    /** Testo delle label dei settori sunburst. */
    label: dark ? '#cbd5e1' : '#3f3f46',
    /** Nodo centrale del sunburst (radice / ritorno dal drilldown). */
    hub: dark ? '#334155' : '#cbd5e1',
    hubLabel: dark ? '#94a3b8' : '#64748b',
    /** Grafo diagnostica: archi e label. */
    treeLine: dark ? '#334155' : '#cbd5e1',
    treeLabel: dark ? '#cbd5e1' : '#334155',
    /** Bordo di enfasi delle celle matrice. */
    cellOutline: dark ? '#94a3b8' : '#0f172a'
  }
}
